import * as React from 'react';
import { observable, action, computed } from 'mobx';

import {
    ICrossSellCategory,
    IProduct,
    IProductSku,
    IShade,
    ISku,
    IVtoEventListenerResult,
    IVtoFoundationPairShadeLabels,
    IVtoFoundationPerfectMatches,
    IVtoFoundationShadeLabels
} from '../interfaces/IVto';
import { IVtoServiceConfig, IVtoTranslations } from '../../exported/vto/Vto';
import { emit, events, on, query, serviceQueries } from 'elc-service-bus';
import { root, BrowserStorage } from 'elc-universal-utils';
import * as VtoConstants from '../constants/Vto';
import { VtoBaseController, IVtoWindow } from './VtoBaseController';
import { IFeature } from '../constants/Features';

declare let window: IVtoWindow;

interface IVtoInitializeController {
    config: IVtoServiceConfig;
    vtoIframeRef: React.RefObject<HTMLDivElement>;
    translations: IVtoTranslations;
}

export class VtoViewController extends VtoBaseController {
    @observable public isVtoShadeGridEnabled: boolean = false;
    @observable public isVtoOpened: boolean = false;
    @observable public isVtoLoaded: boolean = false;
    @observable public isVtoFoundationOverlayOpened: boolean = false;
    @observable public pairProduct?: IProduct;
    @observable public selectedPairSkuId: string;
    @observable public selectedPairShadeName: string = '';
    @observable private selectedSku: ISku;
    @observable public activeTab: string = 'tab-1';
    @observable public skusWithCrossSell: ISku[] = [];
    @observable public isMatchSaved: boolean = false;

    public translations: IVtoTranslations;
    public vtoFoundationPairText: IVtoFoundationPairShadeLabels;

    private togglePerfectShades?: (hidden: boolean) => void;
    private toggleProductImageSection: (isHidden: boolean) => void;
    private selectedSkuId: string;
    private vtoReInitTimeout: number;
    private vtoIframeRef: React.RefObject<HTMLDivElement>;
    private vtoShadeResult?: IVtoFoundationPerfectMatches;

    constructor() {
        super();
        this.checkVtoDisclose();
    }

    public openVtoOnLoad = () => {
        const checkVtoOpenOnLoad = this.checkVtoOpenOnLoad();
        if (checkVtoOpenOnLoad) {
            this.initVto();
        }
    };

    private checkVtoOpenOnLoad(): boolean {
        const params = new URLSearchParams(root.location.search);

        return params.has(VtoConstants.VTO_OPEN_PARAM);
    }

    @action
    public initialize = (props: IVtoInitializeController) => {
        const { config, translations, vtoIframeRef } = props;
        const {
            toggleProductImageSection,
            vtoConfig,
            vtoShadeGridProps,
            isVtoMakeupEnabled,
            isVtoFoundationEnabled,
            selectedSku,
            product,
            handleShadeSelect,
            productIdList
        } = config;
        this.vtoConfig = vtoConfig;
        this.isVtoMakeupEnabled = !!isVtoMakeupEnabled;
        this.isVtoFoundationEnabled = !!isVtoFoundationEnabled;
        this.isVtoFoundationMultiIds = !!this.isVtoFoundationMultiIdsEnabled;
        this.selectedSkuId = selectedSku.skuId;
        this.selectedSku = selectedSku;
        this.product = product;
        this.productIdList = productIdList || [product.productId];
        this.translations = translations;
        this.vtoIframeRef = vtoIframeRef;
        this.vtoFoundationPerfectMatchText = this.getVtoPerfectMatchText();
        this.vtoFoundationPairText = this.getVtoPairText();

        if (handleShadeSelect) {
            this.handleShadeSelect = handleShadeSelect;
        }
        if (vtoShadeGridProps) {
            this.togglePerfectShades = vtoShadeGridProps.togglePerfectShades;
        }
        if (toggleProductImageSection) {
            this.toggleProductImageSection = toggleProductImageSection;
            this.toggleProductImageSection(false);
        }
        this.loadPairProduct();
        this.addVtoEventListeners();
    };

    @computed
    get data() {
        return {
            isVtoShadeGridEnabled: this.isVtoShadeGridEnabled,
            isVtoFoundationOverlayOpened: this.isVtoFoundationOverlayOpened,
            isVtoOpened: this.isVtoOpened,
            pairProduct: this.pairProduct,
            selectedPairSku: this.selectedPairSku,
            selectedPairShade: this.selectedPairShade,
            selectedPairShades: this.selectedPairShades,
            activeTabs: this.activeTabContent,
            selectedPairSizeValue: this.selectedPairSizeValue,
            pairProductLargeImages: this.pairProductLargeImages,
            pairProductUrl: this.pairProductUrl,
            isMatchSaved: this.isMatchSaved
        };
    }

    private getVtoPerfectMatchText = () => {
        return Object.values(VtoConstants.FoundationShadeTypes).reduce((acc, value) => {
            const key = `${value}Label` as keyof IVtoTranslations;

            return {
                ...acc,
                [value]: this.translations[key]
            };
        }, {} as IVtoFoundationShadeLabels);
    };

    private getVtoPairText = () => {
        return Object.values(VtoConstants.FoundationPairShadeTypes).reduce((acc, value) => {
            const key = `${value}Label` as keyof IVtoTranslations;

            return {
                ...acc,
                [value]: this.translations[key]
            };
        }, {} as IVtoFoundationPairShadeLabels);
    };

    private addVtoEventListeners = () => {
        if (typeof window.YMK !== 'object' || this.eventListenerIdentifiers.length) {
            return;
        }
        Object.values(VtoConstants.Events).forEach((event) => {
            const eventListenerIdentifier = window.YMK.addEventListener(event, (result) =>
                this.handleEventListenerCallbacks[event](result as IVtoEventListenerResult)
            );
            this.eventListenerIdentifiers.push(eventListenerIdentifier);
        });
    };

    private handleEventListenerCallbacks: {
        [key in VtoConstants.Events]: (result: IVtoEventListenerResult) => void;
    } = {
        [VtoConstants.Events.LOADING]: () => emit(events.VTO_LOADING),
        [VtoConstants.Events.LOADED]: () => this.handleVtoLoadedEvent(),
        [VtoConstants.Events.OPENED]: () => this.handleVtoOpenedEvent(),
        [VtoConstants.Events.CLOSED]: () => this.handleVtoClosedEvent(),
        [VtoConstants.Events.SKIN_SHADE_FOUND]: (result) => {
            this.handleVtoSkinShadeFoundEvent(result);
            this.handleVtoResize(false);
        },
        [VtoConstants.Events.CAMERA_CLOSED]: () => emit(events.VTO_CAMERA_CLOSED),
        [VtoConstants.Events.CAMERA_FAILED]: () => emit(events.VTO_CAMERA_FAILED),
        [VtoConstants.Events.CAMERA_OPENED]: () => emit(events.VTO_CAMERA_OPENED),
        [VtoConstants.Events.COMPARE]: () => emit(events.VTO_COMPARE),
        [VtoConstants.Events.COMPARE_DISABLED]: () => emit(events.VTO_COMPARE_DISABLED),
        [VtoConstants.Events.ENGINE_CLOSED]: () => emit(events.VTO_ENGINE_CLOSED),
        [VtoConstants.Events.KISS_DETECTED]: () => emit(events.VTO_KISS_DETECTED),
        [VtoConstants.Events.PHOTO_LOADED]: () => emit(events.VTO_PHOTO_LOADED),
        [VtoConstants.Events.PHOTO_SAVED]: () => emit(events.VTO_PHOTO_SAVED),
        [VtoConstants.Events.RESET]: () => {
            emit(events.VTO_RESET);
            this.handleVtoResize(true);
        },
        [VtoConstants.Events.VIDEO_LOADED]: () => emit(events.VTO_VIDEO_LOADED),
        [VtoConstants.Events.SHADE_FINDER_STARTED]: () => this.handleVtoShadeFinderStartedEvent(),
        [VtoConstants.Events.SHADE_FINDER_COMPLETED]: () => emit(events.VTO_SHADE_FINDER_COMPLETED),
        [VtoConstants.Events.SHADE_FINDER_CANCELLED]: () => emit(events.VTO_SHADE_FINDER_CANCELLED),
        [VtoConstants.Events.ZOOM]: () => emit(events.VTO_ZOOM)
    };

    private handleVtoOpenedEvent = () => {
        if (this.isVtoFoundationEnabled && this.isVtoFoundationOverlayEnabled) {
            this.closeVtoFoundationOverlay();
        }
        if (this.isVtoMakeupEnabled) {
            this.toggleProductImageSection(true);
        }
        emit(events.VTO_OPENED);
        this.toggleVtoOpenedState(true);
        this.toggleBodyClasses(true);
        window.addEventListener('resize', this.reInitVto);
    };

    private handleVtoLoadedEvent = () => {
        emit(events.VTO_LOADED);
        this.updateVtoLoadedState(true);
        if (this.isVtoMakeupEnabled) {
            this.toggleVtoShadeGrid(true);
            if (this.selectedSkuId) {
                window.YMK.applyMakeupBySku(this.selectedSkuId);
            }
        }
    };

    private handleVtoShadeFinderStartedEvent = () => {
        const isShadeGridEnabled = false;
        emit(events.VTO_SHADE_FINDER_STARTED);
        this.toggleVtoShadeGrid(isShadeGridEnabled);
    };

    private handleVtoSkinShadeFoundEvent = (result: IVtoEventListenerResult) => {
        this.vtoShadeResult = result.readableRecommendations && result.readableRecommendations[0];
        this.setSaveMatch(false);
        emit(events.VTO_SKIN_SHADE_FOUND);
        this.foundationShadeFilter();
        this.toggleVtoShadeGrid(true);
        this.togglePerfectShades && this.togglePerfectShades(true);
        this.selectPerfectSku(this.vtoShadeResult);
    };

    private handleVtoClosedEvent = () => {
        emit(events.VTO_CLOSED);
        this.toggleVtoOpenedState(false);
        this.toggleBodyClasses(false);
        this.toggleProductImageSection(false);
        this.toggleVtoShadeGrid(false);
        this.resetShadeProps();
        this.togglePerfectShades && this.togglePerfectShades(false);
        this.updateVtoLoadedState(false);
        window.removeEventListener('resize', this.reInitVto);
        if (this.isVtoDisclosed) {
            this.initVto();
        }
    };

    @action
    public toggleVtoOpenedState = (isOpened: boolean) => {
        this.isVtoOpened = isOpened;
    };

    private closeVtoFoundationOverlay = () => {
        this.updateVtoFoundationOverlayState(false);
    };

    @action
    public toggleVtoShadeGrid = (isEnabled: boolean) => {
        this.isVtoShadeGridEnabled = isEnabled;
    };

    @action
    public updateVtoLoadedState = (isLoaded: boolean) => {
        this.isVtoLoaded = isLoaded;
    };

    @action
    public onVtoTabClick = (event: React.MouseEvent<HTMLElement>) => {
        const target = <HTMLElement>event.target;
        this.activeTab = target.dataset.tab || '';

        if (this.activeTab === 'tab-1' && this.togglePerfectShades) {
            this.togglePerfectShades(true);
        } else if (this.activeTab === 'tab-2') {
            emit(events.VTO_PAIR_SHADE_GRID_OPENED);
        } else if (this.activeTab === 'tab-3' && this.togglePerfectShades) {
            this.togglePerfectShades(false);
        }
    };

    @action
    public setPairProduct = (product: IProduct) => {
        this.pairProduct = product;
    };

    @action
    public setSelectedPairSkuId = (skuId: string) => {
        this.selectedPairSkuId = skuId;
    };

    @action
    public setSelectedPairShadeName = (shadeName: string) => {
        this.selectedPairShadeName = shadeName;
    };
    @action
    public setSkusWithCrossSell = (skus: ISku[]) => {
        this.skusWithCrossSell = skus;
    };

    @computed
    public get selectedPairSku() {
        if (!this.pairProduct) {
            return;
        }

        return (
            this.pairProduct.skus.find((sku) => sku.skuId === this.selectedPairSkuId) ||
            this.pairProduct.skus[0]
        );
    }

    @computed
    public get selectedPairShade() {
        return (
            this.selectedPairSku &&
            this.selectedPairSku.shades.find((shade) => shade.name === this.selectedPairShadeName)
        );
    }

    @computed
    public get selectedPairShades() {
        let selectedCrossSell =
            this.skusWithCrossSell[0] && this.skusWithCrossSell[0].crossSellCategory;
        if (
            this.selectedSku &&
            this.selectedSku.crossSellCategory &&
            this.selectedSku.crossSellCategory.length
        ) {
            selectedCrossSell = this.selectedSku.crossSellCategory;
        }

        if (!this.pairProduct || !selectedCrossSell) {
            return [];
        }

        return this.getShadesFromCrossSellProduct(selectedCrossSell);
    }

    private getShadesFromCrossSellProduct = (selectedCrossSell: ICrossSellCategory[]) => {
        return selectedCrossSell.reduce((acc: IShade[], value: ICrossSellCategory) => {
            const crossSellSku =
                this.pairProduct && this.pairProduct.skus.find((sku) => sku.skuId === value.skuId);
            if (crossSellSku && crossSellSku.shades[0]) {
                acc.push(crossSellSku.shades[0]);
            }

            return acc;
        }, []);
    };

    @computed
    get activeTabContent() {
        return {
            perfectShades: this.activeTab === 'tab-1',
            pairShades: this.activeTab === 'tab-2',
            allShades: this.activeTab === 'tab-3'
        };
    }

    @computed
    get selectedPairSizeValue() {
        return (
            (this.selectedPairSku &&
                this.selectedPairSku.sizes &&
                this.selectedPairSku.sizes[0].value) ||
            ''
        );
    }

    @computed
    get pairProductLargeImages() {
        if (!this.pairProduct || !this.selectedPairSku) {
            return [];
        }

        return this.selectedPairSku.media.large.concat(this.pairProduct.media.large);
    }

    @computed
    get pairProductUrl() {
        if (!this.pairProduct) {
            return '';
        }

        return `${this.pairProduct.productUrl}${
            this.selectedPairShade ? `?shade=${this.selectedPairShade.name}` : ''
        }`;
    }

    @computed
    get isVtoFoundationOverlayEnabled() {
        return this.vtoFoundationFeature && this.vtoFoundationFeature.isOverlayEnabled;
    }

    @computed
    get isVtoFoundationMultiIdsEnabled() {
        return this.vtoFoundationFeature && this.vtoFoundationFeature.isMultiIdsEnabled;
    }

    public sortPerfectLabels = (a: IShade, b: IShade) => {
        const orderedPerfectLabels = [
            this.translations.coolerLabel,
            this.translations.lighterLabel,
            this.translations.bestLabel,
            this.translations.darkerLabel,
            this.translations.warmerLabel
        ];

        if (a.perfectShadeLabel && b.perfectShadeLabel && orderedPerfectLabels) {
            const labelA = orderedPerfectLabels.indexOf(a.perfectShadeLabel);
            const labelB = orderedPerfectLabels.indexOf(b.perfectShadeLabel);

            if (labelA < labelB) {
                return -1;
            }

            if (labelA > labelB) {
                return 1;
            }
        }

        return 0;
    };

    private loadPairProduct = async () => {
        const skusWithCrossSell = this.product.skus.filter(
            (sku) => !!(sku.crossSellCategory && sku.crossSellCategory.length)
        );
        const defaultSku = skusWithCrossSell[0];
        const skuIds = skusWithCrossSell.reduce((acc: string[], current: ISku) => {
            if (current.crossSellCategory) {
                current.crossSellCategory.map((sku) => {
                    acc.push(sku.skuId);
                });
            }

            return acc;
        }, []);

        if (defaultSku) {
            const skuData = <IProductSku>await query(serviceQueries.GET_SKUS, skuIds);

            //asumming there's only one pair product match
            const pairProduct = Object.values(skuData)[0].parentProduct;
            this.setSkusWithCrossSell(skusWithCrossSell);
            this.setPairProduct(pairProduct);
        }
    };

    private foundationShadeFilter = () => {
        if (this.vtoShadeResult) {
            const perfectMatches = this.filterPerfectMatches(this.vtoShadeResult);
            this.updateShadeEntity(perfectMatches);
        }
    };

    private reInitVto = () => {
        if (this.vtoReInitTimeout) {
            clearTimeout(this.vtoReInitTimeout);
        }

        this.vtoReInitTimeout = setTimeout(() => {
            const { iframeWidth } = this.getIframeDimension();
            const vtoIframeRef = this.vtoIframeRef && this.vtoIframeRef.current;
            if (iframeWidth !== this.iframeWidth && vtoIframeRef && vtoIframeRef.hasChildNodes()) {
                window.YMK.closeEngine();
                window.YMK.close();
                this.openVto();
            }
        }, VtoConstants.TIMEOUT_VALUE);
    };

    public initVto = () => {
        if (typeof window.ymkAsyncInit !== 'function') {
            const { apiUrl, apiKey } = <IFeature>this.getVtoApiConfig;
            this.loadVto(apiUrl, apiKey);
            this.declareVtoCallback();
        }
        this.openVto();
    };

    private declareVtoCallback = () => {
        window.ymkAsyncInit = () => {
            if (typeof window.YMK !== 'undefined') {
                const vtoParams = this.getVtoParams();
                window.YMK.init(vtoParams);
                this.openVtoFoundation();
                this.addVtoEventListeners();
            }
        };
    };

    private openVtoFoundation() {
        if (this.isVtoFoundationEnabled) {
            this.isVtoFoundationOverlayEnabled
                ? this.showVtoFoundationOverlay()
                : window.YMK.open(true, null, true);
        }
    }

    private openVto = () => {
        this.toggleVtoOpenedState(true);
        this.toggleBodyClasses(true);
        window.ymkAsyncInit();
    };

    private showVtoFoundationOverlay = () => {
        this.updateVtoFoundationOverlayState(true);
    };

    @action
    public updateVtoFoundationOverlayState = (isOverlayOpened: boolean) => {
        this.isVtoFoundationOverlayOpened = isOverlayOpened;
    };

    public handleVtoUnmount = () => {
        this.handleVtoClosedEvent();
        this.removeVtoEventListeners();
    };

    @action
    public onPairShadeSelect = (shadeName: string) => {
        const selectedPairShade =
            this.pairProduct && this.pairProduct.shades.find((shade) => shade.name === shadeName);
        const selectedPairSku = selectedPairShade && selectedPairShade.parentSku;

        if (this.isVtoLoaded && selectedPairSku) {
            this.setSelectedPairSkuId(selectedPairSku.skuId);
            this.setSelectedPairShadeName(shadeName);
            emit(events.VTO_PAIR_SHADE_SELECTED);
            window.YMK.applyMakeupBySku(selectedPairSku.skuId);
        }
    };

    @on(events.PRODUCT_SHADE_SELECTED)
    public vtoMakeupApply = (payload: { skuId: string }) => {
        if (this.isVtoLoaded) {
            window.YMK.applyMakeupBySku(payload.skuId);
        }
    };

    @on(events.VTO_FOUNDATION_OVERLAY_TOGGLED)
    public vtoFoundationOverlayToggled = (payload: { isOverlayOpened: boolean }) => {
        this.updateVtoFoundationOverlayState(payload.isOverlayOpened);
        this.toggleVtoOpenedState(false);
        this.toggleBodyClasses(false);
    };

    @action
    public updateSelectedSku = (sku: ISku) => {
        this.selectedSku = sku;
    };

    @action
    public setSaveMatch = (isSaved: boolean) => {
        this.isMatchSaved = isSaved;
    };

    public saveMatches = () => {
        const shadeMatches = this.getShadeMatches();
        if (!shadeMatches) {
            return;
        }

        const bestSku = this.product.skus.find(
            (sku) => sku.skuId === shadeMatches[VtoConstants.FoundationShadeTypes.BEST]
        );

        if (!bestSku) {
            return;
        }

        bestSku.selectedShade.setBestShade();
        this.setSaveMatch(true);
        this.setVtoMatchCookie(shadeMatches);
        emit(events.VTO_MATCH_SAVED);
    };

    private getShadeMatches() {
        if (!this.vtoShadeResult) {
            return null;
        }
        const shadeTypes = VtoConstants.FoundationShadeTypes;
        const shadeTypeValues = Object.values(shadeTypes);

        return Object.entries(this.vtoShadeResult).reduce((acc, [type, skuId]) => {
            const matchType =
                type === VtoConstants.VTO_GUID
                    ? shadeTypes.BEST
                    : (type as VtoConstants.FoundationShadeTypes);

            if (shadeTypeValues.indexOf(matchType) > -1) {
                return { ...acc, [matchType]: skuId };
            }

            return acc;
        }, {} as IVtoFoundationPerfectMatches);
    }

    private setVtoMatchCookie(shadeMatches: IVtoFoundationPerfectMatches) {
        const cookieName = VtoConstants.VTO_MATCHES_COOKIE_NAME;
        const shadeMatchCookie = BrowserStorage.getItem(cookieName);
        let newShadeMatches = {};

        if (shadeMatchCookie) {
            newShadeMatches = JSON.parse(shadeMatchCookie);
        }

        BrowserStorage.setItem(
            cookieName,
            JSON.stringify({ ...newShadeMatches, [this.product.productId]: shadeMatches })
        );
    }

    public removeMatches = () => {
        const cookieName = VtoConstants.VTO_MATCHES_COOKIE_NAME;
        const shadeMatchCookie = BrowserStorage.getItem(cookieName);
        if (!shadeMatchCookie) {
            return;
        }
        const shadeMatches = JSON.parse(shadeMatchCookie);
        delete shadeMatches[this.product.productId];
        BrowserStorage.setItem(cookieName, JSON.stringify(shadeMatches));
        this.setSaveMatch(false);
        emit(events.VTO_MATCH_REMOVED);
    };
}
